import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import get from "lodash/get";

import { AccessType, ResponsiveHeroImage, ReviewRating, SocialShare } from "@quintype/components";
import { StoryHeaderCard } from "../story-page-components/story-header-card.js";
import { StoryTags } from "../story-page-components/story-tags.js";
import { RelatedStories } from "../story-page-components/related-stories.js";
import { DfpAd } from "../dfp-ad";
import { MetypeCommentingWidget } from "@metype/components";
import { breakpoint } from "../../../utils/breakpoint";
import { addTargetBlankToExtLinks } from "../../../utils/add-target-blank-to-extlinks";
import { ImageFallback } from "../../components/basic/image-fallback";
import { altTagOfHeroImages, getSocialShareProps, getSocialShareStyle, isEmpty } from "../../utils";
import { VerticalGrey } from "../../components/story-page-components/social-share/vertical/vertical-grey";
import { VerticalColor } from "../../components/story-page-components/social-share/vertical/vertical-color";
import { Separator } from "../../components/basic/separator-line";
import { getAccessData, getStoryCards, handleAccess, checkIfStoryPublic } from "../sso-components/sso-utils";

import "./blank.m.css";

function renderMetype(story, metypeConfig = {}, previewStory = false) {
  if (previewStory || isEmpty(metypeConfig)) return null;

  return (
    <div className="blank-story--metype-widget">
      <MetypeCommentingWidget
        host={metypeConfig.host}
        accountId={parseInt(metypeConfig.accountId)}
        pageURL={generateHostUrl(story)}
        primaryColor={metypeConfig.primaryColor}
      />
    </div>
  );
}

function getTextStoryClass(textStory) {
  switch (textStory) {
    case "photo-priority":
      return "photo-priority-header";
    case "photo-low-priority":
      return "low-photo-priority-header";
    default:
      return "";
  }
}

function handlePhotoLowPriority(socialShareStyle) {
  switch (socialShareStyle) {
    case "horizontal-grey":
      return VerticalGrey;
    case "horizontal-color":
      return VerticalColor;
    case "vertical-grey":
      return VerticalGrey;
    case "vertical-color":
      return VerticalColor;
    default:
      return VerticalGrey;
  }
}

function handlePhotoPriority(socialShareStyle) {
  switch (socialShareStyle) {
    case "vertical-color":
      return VerticalColor;
    case "vertical-grey":
      return VerticalGrey;
    default:
      return () => null;
  }
}

function renderSocialShare(config, story, isVertical, textStory, isReviewStory, defaultStyle = "horizontal-color") {
  const socialShareProps = getSocialShareProps(config, story);
  const socialShareStyle = get(config, ["theme-attributes", "social-share-style"], defaultStyle);
  switch (textStory) {
    case "photo-low-priority":
      return (
        <div className="story-byline__social-share">
          <SocialShare template={handlePhotoLowPriority(socialShareStyle)} {...socialShareProps} />
        </div>
      );
    case "photo-priority":
      return (
        <div className="story-byline__social-share">
          <SocialShare template={handlePhotoPriority(socialShareStyle)} {...socialShareProps} />
        </div>
      );
  }
  return null;
}

function BlankStoryTemplate(props) {
  const metypeConfig = get(props, ["config", "theme-attributes", "metype-config"], {});
  const textStory = get(props, ["config", "theme-attributes", "text-story"], "default");
  const headerTemplate = get(props, ["config", "theme-attributes", "text-story"], "");

  const isReviewStory = props.story["story-template"] === "review";
  const haveFocusPoint = !!get(props, ["story", "hero-image-metadata", "focus-point"]);
  const imageElement = breakpoint("tablet") ? (
    <ResponsiveHeroImage
      story={props.story}
      aspectRatio={[16, 9]}
      alt={altTagOfHeroImages(props.story)}
      defaultWidth={480}
      widths={[250, 480, 640]}
      sizes="(max-width: 500px) 98%, (max-width: 768px) 48%, 98%"
      imgParams={{ auto: ["format", "compress"], fit: "max" }}
    />
  ) : (
    <ResponsiveHeroImage
      story={props.story}
      aspectRatio={textStory === "default" ? [8, 3] : [16, 9]}
      alt={altTagOfHeroImages(props.story)}
      defaultWidth={480}
      widths={[250, 480, 640, 960, 1200]}
      sizes="(max-width: 500px) 98%"
      imgParams={{ auto: ["format", "compress"], fit: "max" }}
    />
  );

  function headerTemplateLayout() {
    return (
      <div className="image-content-wrapper">
        {props.story["hero-image-s3-key"] ? (
          <figure
            className="blank-story-hero-image qt-responsive-image"
            styleName={!haveFocusPoint ? "full-height" : ""}
          >
            {imageElement}
          </figure>
        ) : (
          <div className="blank-story-hero-image__fallback-placeholder" />
        )}
      </div>
    );
  }

  function getPhotoLowPriorityTemp() {
    if (headerTemplate === "photo-low-priority" || isReviewStory) {
      return (
        <div className="photo-low-priority-wrapper">
          {props.story["hero-image-s3-key"] ? (
            <figure className="blank-story-hero-image qt-responsive-image">{imageElement}</figure>
          ) : (
            <div className="blank-story-hero-image__fallback-placeholder">
              <ImageFallback />
            </div>
          )}
          {!isReviewStory && (
            <StoryHeaderCard story={props.story} config={props.config} textStory={!isReviewStory && textStory} />
          )}
        </div>
      );
    }
  }

  function getTemplate() {
    const headerTemplate = get(props, ["config", "theme-attributes", "text-story"], "");
    switch (headerTemplate) {
      case "photo-priority":
        return headerTemplateLayout();
      case "default":
        return headerTemplateLayout();
      case "photo-low-priority":
        return getPhotoLowPriorityTemp();
      default:
        return headerTemplateLayout();
    }
  }

  function getCaption() {
    const caption = get(props, ["story", "hero-image-caption"], "");
    const attribution = get(props, ["story", "hero-image-attribution"], "");
    return (
      <div styleName="image-caption">
        <span dangerouslySetInnerHTML={{ __html: caption }} />
        {caption && attribution ? <Separator fill="#000" /> : null}
        <span dangerouslySetInnerHTML={{ __html: attribution }} />
      </div>
    );
  }

  function renderCaption(textStory) {
    switch (textStory) {
      case "photo-priority":
        return getCaption();
      case "photo-low-priority":
        return getCaption();
      default:
        return null;
    }
  }

  const socialShareStyle = getSocialShareStyle(props.config);

  const storyAccess = get(props, ["access"]);
  const accessLoading = get(props, ["accessLoading"]);

  const isStoryPublic = checkIfStoryPublic(props.story);
  const cdnImage = get(props, ["config", "cdn-image"], "");

  const isVertical =
    !isReviewStory &&
    (textStory === "photo-priority" || textStory === "photo-low-priority") &&
    socialShareStyle.includes("vertical");
  const iconType =
    (get(props.config, ["theme-attributes", "social-share-style"]) === "horizontal-grey" ||
      get(props.config, ["theme-attributes", "social-share-style"]) === "horizontal-color") &&
    textStory === "photo-low-priority" &&
    !isReviewStory;

  const storyCards = get(props, ["story", "cards"], []);

  return (
    <article
      className="blank-story"
      styleName={`${isVertical || iconType ? "vertical" : ""} ${!isReviewStory ? getTextStoryClass(textStory) : ""}`}
    >
      {getTemplate()}
      <div className="blank-story--wrapper">
        <div styleName="blank-story-content-wrapper">
          {!isReviewStory && (
            <div styleName="social-share-sticky">
              {renderSocialShare(props.config, props.story, isVertical, textStory, isReviewStory)}
            </div>
          )}
          <div className="blank-story--content">
            {renderCaption(textStory)}
            {(headerTemplate !== "photo-low-priority" || isReviewStory) && (
              <StoryHeaderCard story={props.story} config={props.config} textStory={!isReviewStory && textStory} />
            )}
            {isReviewStory && (
              <div className="rating__wrapper" data-test-id="rating-wrapper">
                <h5 className="rating__title">
                  {get(props.story, ["metadata", "review-title"])}{" "}
                  <span className="rating__value" data-test-id="rating-value">
                    ({get(props.story, ["metadata", "review-rating", "value"])} / 5)
                  </span>
                </h5>
                <div className="rating">
                  <ReviewRating value={get(props.story, ["metadata", "review-rating", "value"], "0")} />
                </div>
              </div>
            )}
            {isStoryPublic
              ? getStoryCards(storyCards, props.story, cdnImage)
              : handleAccess(
                  props.story,
                  storyAccess,
                  accessLoading,
                  props.config
                ) /* Method to check for access of the user */}
            <StoryTags tags={props.story.tags} config={props.config} />
            <div className="blank-story__aside">
              {textStory === "default" && (
                <div className="app-ad app-ad--story-mrec">
                  <DfpAd adtype="Story-Mrec" />
                </div>
              )}
              {(isReviewStory || textStory === "default") && <RelatedStories story={props.story} />}
            </div>
          </div>
        </div>
        {textStory === "photo-priority" && !isReviewStory && (
          <RelatedStories story={props.story} config={props.config} />
        )}
        {!props.preview && (
          <div>
            <div className="app-ad app-ad--story-horizontal">
              <DfpAd adtype="Story-Bottom-Ad" />
            </div>
            {renderMetype(props.story, metypeConfig, props.preview)}
          </div>
        )}
      </div>
    </article>
  );
}

BlankStoryTemplate.propTypes = {
  story: PropTypes.object,
  config: PropTypes.object,
  preview: PropTypes.bool
};

function generateHostUrl(story = {}) {
  if (global.location) {
    return `${global.location.origin}/${story.slug}`;
  }
}

class BlankStoryAccessWrapper extends React.Component {
  componentDidMount() {
    this.props.initAccessType();
    const { checkAccess, story, accessUpdated, accessIsLoading, config } = this.props;
    getAccessData(checkAccess, story, accessUpdated, accessIsLoading, config);
    addTargetBlankToExtLinks(this.storyWrapperElement);
  }

  render() {
    const storyMetadata = get(this.props, ["story", "metadata"], {});
    return (
      <div className={`story-grid`} ref={el => (this.storyWrapperElement = el)}>
        <div style={{ display: "none" }} className="blank-story-metadata">
          {JSON.stringify(storyMetadata)}
        </div>
        {/* DO NOT DELETE, HACK ON PURPOSE. */}
        <BlankStoryTemplate {...this.props} />
      </div>
    );
  }
}

BlankStoryAccessWrapper.propTypes = {
  story: PropTypes.object,
  config: PropTypes.object,
  preview: PropTypes.bool,
  access: PropTypes.object,
  checkAccess: PropTypes.func,
  initAccessType: PropTypes.func,
  accessUpdated: PropTypes.func,
  accessIsLoading: PropTypes.func
};

class BlankStoryBase extends React.Component {
  render() {
    const email = get(this.props, ["member", "email"]);
    const phone = get(this.props, ["member", "metadata", "phone-number"], "");
    const accessTypeKey = get(this.props, ["config", "theme-attributes", "accesstypeKey"]);
    const disableMetering = get(this.props, ["config", "theme-attributes", "disableMetering"]) === "true";
    const isStaging = get(this.props, ["config", "theme-attributes", "accesstypeEnvironment"]) === "staging";
    const enableAccesstype = get(this.props, ["config", "theme-attributes", "enableAccesstype"]) === "true";

    return (
      <AccessType
        enableAccesstype={enableAccesstype}
        isStaging={isStaging}
        accessTypeKey={accessTypeKey} // "9rm1ijqQmGfJ2kwQ9zt2gWhK"
        email={email}
        phone={phone}
        disableMetering={disableMetering}
      >
        {({ initAccessType, checkAccess, accessUpdated, accessIsLoading }) => (
          <div className={`story-grid`} ref={el => (this.storyWrapperElement = el)}>
            <BlankStoryAccessWrapper
              accessIsLoading={accessIsLoading}
              accessUpdated={accessUpdated}
              initAccessType={initAccessType}
              checkAccess={checkAccess}
              {...this.props}
            />
          </div>
        )}
      </AccessType>
    );
  }
}

const mapStateToProps = state => ({
  member: get(state, ["member"], null),
  access: get(state, ["access"], null),
  accessLoading: get(state, ["accessLoading"], true)
});

export const BlankStory = connect(mapStateToProps)(BlankStoryBase);
